import * as React from 'react';
import { styled } from '@stitches/react';
import { faClose, faGripDotsVertical, faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Input } from '@/components/form';
import { v4 as uuid } from 'uuid';
import NumberFormat from '@/components/common/NumberFormat';
import Typography from '@/components/sushi/Typography';
import { IconButton } from '@/components/IconButton/IconButton';
import { createTransition } from '@/utils/css/css';
import { useIntl } from 'react-intl';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableHandle, SortableItem } from './SortableItem';

export type CategoryValue = { id: string; name?: string; value?: string };

export type Category = {
  id: string;
  name: string;
  values: CategoryValue[];
};

export type RootCategory = { id: string; name: string; i18nKey: string; categories: Category[] };

export type RootCategoryFormProps = {
  single?: boolean;
  values: RootCategory;
  setValues: React.Dispatch<React.SetStateAction<RootCategory>>;
};

/**
 * Remove the category at given index
 */
export type GetHandleDeleteCategory = (categoryIndex: number) => () => void;

/**
 * Change the name of the category at given index
 */
export type GetHandleChangeCategoryName = (
  categoryIndex: number
) => React.ChangeEventHandler<HTMLInputElement>;

/**
 * Add a new category at given index
 */
export type GetHandleAddCategory = (newCategoryIndex?: number) => () => void;

/**
 * Change the given property of the category entry at given index
 */
export type GetHandleChangeCategoryEntry = (
  categoryIndex: number,
  index: number,
  property: keyof CategoryValue
) => React.ChangeEventHandler<HTMLInputElement>;

/**
 * Remove the category entry at given index
 */
export type GetHandleRemoveCategoryEntry = (
  categoryIndex: number,
  index: number
) => React.MouseEventHandler;

/**
 * Add a new category entry at given index
 */
export type GetHandleAddCategoryEntry = (
  categoryIndex: number,
  newEntryIndex?: number
) => React.MouseEventHandler;

/**
 * RootCategoryForm context
 */
export const RootCategoryFormContext = React.createContext<{
  getHandleDeleteCategory: GetHandleDeleteCategory;
  getHandleChangeCategoryName: GetHandleChangeCategoryName;
  getHandleAddCategory: GetHandleAddCategory;
  getHandleChangeCategoryEntry: GetHandleChangeCategoryEntry;
  getHandleRemoveCategoryEntry: GetHandleRemoveCategoryEntry;
  getHandleAddCategoryEntry: GetHandleAddCategoryEntry;
  setValues: React.Dispatch<React.SetStateAction<RootCategory>>;
}>({
  getHandleDeleteCategory: () => () => undefined,
  getHandleChangeCategoryName: () => () => undefined,
  getHandleAddCategory: () => () => undefined,
  getHandleChangeCategoryEntry: () => () => undefined,
  getHandleRemoveCategoryEntry: () => () => undefined,
  getHandleAddCategoryEntry: () => () => undefined,
  setValues: () => undefined,
});

const useRootCategoryFormContext = () => React.useContext(RootCategoryFormContext);

export const RootCategoryForm = ({ single, values, setValues }: RootCategoryFormProps) => {
  const { formatMessage } = useIntl();
  const [dragId, setDragId] = React.useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getHandleDeleteCategory = (categoryIndex: number) => () => {
    setValues((previousValues) => {
      const newValues = { ...previousValues };
      newValues.categories = newValues.categories.filter((_, index) => index !== categoryIndex);
      return newValues;
    });
  };

  const getHandleChangeCategoryName =
    (categoryIndex: number): React.ChangeEventHandler<HTMLInputElement> =>
    (event) => {
      setValues((previousValues) => {
        const newRoot = { ...previousValues };
        const newCategories = newRoot.categories;
        const newCategory = newCategories[categoryIndex];
        newCategory.name = event.target.value;
        newCategories[categoryIndex] = newCategory;
        newRoot.categories = newCategories;
        return newRoot;
      });
    };

  const getHandleAddCategory = (newCategoryIndex?: number) => () => {
    setValues((previousValues) => {
      const newRoot = { ...previousValues };
      const sliceIndex = newCategoryIndex ?? newRoot.categories.length;
      const precedingCategories = newRoot.categories.slice(0, sliceIndex);
      const followingCategories = newRoot.categories.slice(sliceIndex);
      const newCategory = {
        id: uuid(),
        name: formatMessage({ id: `budget_calculator.form.${values.i18nKey}.new_group` }),
        values: [{ id: uuid() }],
      };
      newRoot.categories = [...precedingCategories, newCategory, ...followingCategories];
      return newRoot;
    });
  };

  const getHandleChangeCategoryEntry =
    (
      categoryIndex: number,
      index: number,
      property: keyof CategoryValue
    ): React.ChangeEventHandler<HTMLInputElement> =>
    (event) => {
      setValues((previousValues) => {
        const newRoot = { ...previousValues };
        const newCategories = newRoot.categories;
        const newCategory = newCategories[categoryIndex];
        const newValues = [...newCategory.values];
        newValues[index][property] = event.target.value;
        newCategory.values = newValues;
        newCategories[categoryIndex] = newCategory;
        newRoot.categories = newCategories;
        return newRoot;
      });
    };

  const getHandleRemoveCategoryEntry =
    (categoryIndex: number, index: number): React.MouseEventHandler =>
    () => {
      setValues((previousValues) => {
        const newValues = { ...previousValues };
        const newCategories = newValues.categories;
        const newCategory = newCategories[categoryIndex];
        newCategory.values = newCategory.values.filter((_, idx) => index !== idx);
        newCategories[categoryIndex] = newCategory;
        newValues.categories = newCategories;
        return newValues;
      });
    };

  const getHandleAddCategoryEntry =
    (categoryIndex: number, newEntryIndex?: number): React.MouseEventHandler =>
    () => {
      setValues((previousValues) => {
        const newRoot = { ...previousValues };
        const newCategories = newRoot.categories;
        const newCategory = newCategories[categoryIndex];
        const sliceIndex = newEntryIndex ?? newCategory.values.length;
        const precedingValues = newCategory.values.slice(0, sliceIndex);
        const followingValues = newCategory.values.slice(sliceIndex);
        const newValue = { id: uuid() };
        newCategory.values = [...precedingValues, newValue, ...followingValues];
        newCategories[categoryIndex] = newCategory;
        newRoot.categories = newCategories;
        return newRoot;
      });
    };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setValues((items) => {
        const { categories } = items;
        const oldIndex = categories.findIndex((item) => item.id === active.id);
        const newIndex = categories.findIndex((item) => item.id === over.id);

        const newCategories = arrayMove(categories, oldIndex, newIndex);
        return { ...items, categories: newCategories };
      });
    }

    setDragId(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setDragId(event.active.id);
  };

  const draggedCategory = values.categories.find((category) => category.id === dragId);
  const draggedCategoryIndex = values.categories.findIndex((category) => category.id === dragId);

  return (
    <RootCategoryFormContext.Provider
      value={{
        getHandleDeleteCategory,
        getHandleChangeCategoryName,
        getHandleAddCategory,
        getHandleChangeCategoryEntry,
        getHandleRemoveCategoryEntry,
        getHandleAddCategoryEntry,
        setValues,
      }}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragCancel={() => setDragId(null)}
        onDragEnd={handleDragEnd}>
        <SortableContext
          key={`${values.id}-${values.categories.length}`}
          items={values.categories}
          strategy={verticalListSortingStrategy}>
          <FormContainer>
            {values.categories.map((category, categoryIndex) => (
              <SortableItem key={category.id} id={category.id}>
                <CategoryCard
                  single={single}
                  orphan={values.categories.length === 1}
                  i18nKey={values.i18nKey}
                  category={category}
                  categoryIndex={categoryIndex}
                  isBeingDragged={category.id === dragId}
                />
              </SortableItem>
            ))}
            {!single ? (
              <Typography variant={'bodyS'}>
                <AddCategoryButton onClick={getHandleAddCategory()}>
                  {formatMessage({ id: `budget_calculator.form.${values.i18nKey}.add_group` })}{' '}
                  <FontAwesomeIcon icon={faPlus} size="1x" className={'ml-1 mr-0'} />
                </AddCategoryButton>
              </Typography>
            ) : null}
          </FormContainer>
        </SortableContext>
        <DragOverlay>
          {dragId ? (
            <CategoryCard
              single={single}
              i18nKey={values.i18nKey}
              category={draggedCategory}
              categoryIndex={draggedCategoryIndex}
              isDragOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    </RootCategoryFormContext.Provider>
  );
};

export const CategoryCard = ({
  single,
  orphan,
  i18nKey,
  category,
  categoryIndex,
  isBeingDragged,
  isDragOverlay,
}: {
  single?: boolean;
  orphan?: boolean;
  i18nKey: string;
  category: Category;
  categoryIndex: number;
  isBeingDragged?: boolean;
  isDragOverlay?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const [dragId, setDragId] = React.useState(null);
  const {
    getHandleDeleteCategory,
    getHandleAddCategory,
    getHandleAddCategoryEntry,
    getHandleChangeCategoryName,
    setValues,
  } = useRootCategoryFormContext();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setValues((items) => {
        const { categories } = items;
        const prevCategories = categories.slice(0, categoryIndex);
        const nextCategories = categories.slice(categoryIndex + 1);
        const newCategory = { ...category };
        const oldIndex = category.values.findIndex((item) => item.id === active.id);
        const newIndex = category.values.findIndex((item) => item.id === over.id);
        newCategory.values = arrayMove(category.values, oldIndex, newIndex);
        return { ...items, categories: [...prevCategories, newCategory, ...nextCategories] };
      });
    }

    setDragId(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setDragId(event.active.id);
  };

  const getCategoryTotal = (category: Category): number => {
    return category.values
      .map((values) => parseFloat(values.value ?? ''))
      .filter(Boolean)
      .filter((number) => !isNaN(number))
      .reduce((acc, val) => acc + val, 0);
  };

  const draggedEntry = category.values.find((entry) => entry.id === dragId);
  const draggedEntryIndex = category.values.findIndex((entry) => entry.id === dragId);

  return (
    <Card key={category.id} isBeingDragged={isBeingDragged} isDragOverlay={isDragOverlay}>
      {!single ? (
        <CardControls
          style={
            {
              visibility: isBeingDragged ? 'hidden' : undefined,
              '--transition-duration': isDragOverlay ? '0s' : undefined,
              '--transition-delay': isDragOverlay ? '0s' : undefined,
            } as React.CSSProperties
          }>
          {!orphan ? (
            <>
              <CardControlListElement>
                <SortableHandle id={category.id}>
                  <IconButton as={CardControl} size={'small'} variant={'secondary'} cursor={'grab'}>
                    <FontAwesomeIcon icon={faGripDotsVertical} size="sm" className={'mr-0'} />
                  </IconButton>
                </SortableHandle>
              </CardControlListElement>
              <CardControlListElement>
                <IconButton
                  as={CardControl}
                  size={'small'}
                  variant={'secondary'}
                  onClick={getHandleDeleteCategory(categoryIndex)}>
                  <FontAwesomeIcon icon={faTrash} size="sm" className={'mr-0'} />
                </IconButton>
              </CardControlListElement>
            </>
          ) : null}
          <CardControlListElement>
            <IconButton
              as={CardControl}
              size={'small'}
              variant={'secondary'}
              onClick={getHandleAddCategory(categoryIndex + 1)}>
              <FontAwesomeIcon icon={faPlus} size="sm" className={'mr-0'} />
            </IconButton>
          </CardControlListElement>
        </CardControls>
      ) : null}
      <CardHeader
        onClick={(event) => {
          const input = event.currentTarget.querySelector('input');
          if (input) {
            input.focus();
          }
        }}>
        <CardHeaderInput
          type="text"
          value={category.name}
          onChange={getHandleChangeCategoryName(categoryIndex)}
        />
        <Typography
          variant={'bodyM'}
          color={'webappTertiary'}
          style={{ width: '6em', textAlign: 'right' }}>
          <NumberFormat value={getCategoryTotal(category)} type="currency" currency="EUR" />
        </Typography>
      </CardHeader>
      <CardBody>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragCancel={() => setDragId(null)}
          onDragEnd={handleDragEnd}>
          <SortableContext
            key={`${category.id}-${category.values.length}`}
            items={category.values}
            strategy={verticalListSortingStrategy}>
            {category.values.map((value, index) => (
              <SortableItem key={value.id} id={value.id}>
                <CategoryCardEntry
                  orphan={category.values.length === 1}
                  value={value}
                  index={index}
                  categoryIndex={categoryIndex}
                  isBeingDragged={dragId === value.id}
                />
              </SortableItem>
            ))}
          </SortableContext>
          <DragOverlay>
            {dragId ? (
              <CategoryCardEntry
                isDragOverlay
                orphan={category.values.length === 1}
                value={draggedEntry}
                index={draggedEntryIndex}
                categoryIndex={categoryIndex}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </CardBody>
      <CardFooter>
        <Typography variant={'bodyS'}>
          <CardFooterButton onClick={getHandleAddCategoryEntry(categoryIndex)}>
            {formatMessage({ id: `budget_calculator.form.${i18nKey}.add_entry` })}{' '}
            <FontAwesomeIcon icon={faPlus} size="1x" className={'ml-1 mr-0'} />
          </CardFooterButton>
        </Typography>
      </CardFooter>
    </Card>
  );
};

const CategoryCardEntry = ({
  orphan,
  value,
  index,
  categoryIndex,
  isBeingDragged,
  isDragOverlay,
}: {
  orphan?: boolean;
  value: CategoryValue;
  index: number;
  categoryIndex: number;
  isBeingDragged?: boolean;
  isDragOverlay?: boolean;
}) => {
  const { formatMessage } = useIntl();
  const { getHandleChangeCategoryEntry, getHandleRemoveCategoryEntry } =
    useRootCategoryFormContext();

  return (
    <CardEntry key={value.id} isDragOverlay={isDragOverlay} isBeingDragged={isBeingDragged}>
      <SortableHandle id={value.id}>
        <CardEntryControl
          style={{
            marginTop: 7,
            marginLeft: -8,
            marginRight: 0,
          }}>
          <FontAwesomeIcon icon={faGripDotsVertical} size="sm" className={'mr-0'} />
        </CardEntryControl>
      </SortableHandle>

      <Input
        label={formatMessage({ id: `budget_calculator.form.entry.name_label` })}
        name={`name-${value.id}`}
        type="text"
        value={value.name ?? ''}
        onChange={getHandleChangeCategoryEntry(categoryIndex, index, 'name')}
        style={{ flexGrow: 1 }}
      />
      <Input
        label={formatMessage({ id: `budget_calculator.form.entry.value_label` })}
        name={`value-${value.id}`}
        type="number"
        value={value.value ?? ''}
        onChange={getHandleChangeCategoryEntry(categoryIndex, index, 'value')}
        style={{ flexGrow: 1 }}
        suffix="EUR"
      />
      {!orphan ? (
        <IconButton
          size={'medium'}
          variant={'quaternary'}
          onClick={getHandleRemoveCategoryEntry(categoryIndex, index)}
          style={{
            marginTop: 7,
          }}>
          <FontAwesomeIcon icon={faClose} size="sm" className={'mr-0'} />
        </IconButton>
      ) : null}
    </CardEntry>
  );
};

const CardControls = styled('ul', {
  '--transition-duration': '0.1s',
  '--transform': 'translateX(-100%) scale(0)',
  '--opacity': 0,
  listStyleType: 'none',
  padding: 0,
  margin: 0,
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 'calc(100%)',
  paddingLeft: 8,
  width: 48,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
});

const FormContainer = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginTop: 32,

  [`& div[role=button]:focus ${CardControls}`]: {
    outline: 'none',
    '--transform': '0',
    '--opacity': 1,
  },
});

const Card = styled('article', {
  background: `$colorBackground`,
  color: '$textPrimary',
  borderRadius: '$4',
  border: '1px solid $borderSecondary',
  width: '100%',
  position: 'relative',
  cursor: 'default',

  [`&:is(:focus-within, :hover) ${CardControls}`]: {
    '--transform': '0',
    '--opacity': 1,
  },

  variants: {
    isBeingDragged: {
      true: {
        opacity: 0.35,
      },
    },
    isDragOverlay: {
      true: {
        borderColor: '$borderPrimary',
        boxShadow: '2px 2px 16px 0 rgba(0, 0, 0, 0.5)',
      },
    },
  },
});

const CardHeader = styled('header', {
  padding: 24,
  borderBottom: '1px solid $borderSecondary',
  display: 'flex',
  alignItems: 'center',
  background: `$colorBackground`,
  borderTopLeftRadius: 'inherit',
  borderTopRightRadius: 'inherit',
});

const CardHeaderInput = styled('input', {
  appearance: 'none',
  backgroundColor: 'transparent',
  border: 'none',
  flexGrow: 1,
  padding: 0,
  fontWeight: 500,

  '&:focus': {
    outline: 'none',
  },
});

const CardControlListElement = styled('li', {
  '&:nth-child(1)': {
    '--control-transition-delay': 0,
  },

  '&:nth-child(2)': {
    '--control-transition-delay': '0.05s',
  },

  '&:nth-child(3)': {
    '--control-transition-delay': '0.1s',
  },
});

const CardControl = styled('button', {
  transform: 'var(--transform)',
  opacity: 'var(--opacity)',
  position: 'relative',
  transition: createTransition(
    ['transform', 'opacity', 'color', 'background-color', 'border-color'],
    'var(--transition-duration)',
    'ease-in-out'
  ),
  transitionDelay: 'var(--transition-delay, var(--control-transition-delay))',

  '&:focus-visible': {
    outline: 'none',
  },
});

const CardBody = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  padding: 24,
  width: '100%',
});

const CardFooter = styled('footer', {
  display: 'flex',
  justifyContent: 'flex-start',
  padding: '0 24px 24px 24px',
  width: '100%',
});

const CardFooterButton = styled('button', {
  cursor: 'pointer',
  color: '$webappTertiary',
  transition: createTransition(['color'], '0.15s', 'ease'),

  '&:hover, &:focus-visible': {
    color: '$webappSecondary',
    outline: 'none',
  },
});

const CardEntryControl = styled('button', {
  color: `$webappTertiary`,
  transition: createTransition(['color', 'background-color'], '0.15s', 'ease-in-out'),
  width: 32,
  cursor: 'grab',
  touchAction: 'none',

  '&:hover, &:focus-visible': {
    color: '$webappSecondary',
    outline: 'none',
  },
});

const CardEntry = styled('div', {
  gap: 8,
  display: 'flex',
  alignItems: 'center',
  width: '100%',

  variants: {
    isBeingDragged: {
      true: {
        opacity: 0,
      },
    },
    isDragOverlay: {
      true: {
        background: `$colorBackground`,
        [`& ${CardEntryControl}`]: {
          color: '$webappSecondary',
        },
      },
    },
  },
});

const AddCategoryButton = styled('button', {
  width: '100%',
  borderRadius: '$4',
  padding: 24,
  border: '1px dashed $borderSecondary',
  color: '$textSecondaryAlt',
  transition: createTransition(['color', 'border'], '0.15s', 'ease-in-out'),

  '&:hover, &:focus-visible': {
    border: '1px dashed $borderPrimary',
    color: '$textSecondary',
  },

  '&:focus-visible': {
    backgroundColor: '$surfaceSecondary',
    outline: 'none',
  },
});
